import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Navigate } from '@sorare/routing';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { League } from '@sorare/core/src/components/landing/types';
import { ANY_SPORT_HOME } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';

import { WalletLanding } from 'components/landing/WalletLanding';

import heroBgAvif from './assets/hero/bg.avif';
import heroBg from './assets/hero/bg.webp';
import leagueLogo from './assets/hero/logo.svg';
import heroPlayersLeftSideAvif from './assets/hero/players/leftSidePlayers.avif';
import heroPlayersLeftSide from './assets/hero/players/leftSidePlayers.webp';
import heroPlayersRightSideAvif from './assets/hero/players/rightSidePlayers.avif';
import heroPlayersRightSide from './assets/hero/players/rightSidePlayers.webp';
import playBackgroundAvif from './assets/play/background.avif';
import playBackground from './assets/play/background.webp';
import lineupAvif from './assets/play/lineup.avif';
import lineup from './assets/play/lineup.webp';
import scoutBgAvif from './assets/scout/background.avif';
import scoutBg from './assets/scout/background.webp';
import scoutPlayersAvif from './assets/scout/players.avif';
import scoutPlayers from './assets/scout/players.webp';
import allStars from './assets/win/allStars.webp';
import box from './assets/win/box.webp';
import coin from './assets/win/coin.webp';
import jersey from './assets/win/jersey.webp';
import plane from './assets/win/plane.webp';
import cards from './assets/win/rewardCards.webp';
import ticket from './assets/win/ticket.webp';

const messages = defineMessages({
  metadatasTitle: {
    id: 'Landing.NBA.metadatasTitle',
    defaultMessage:
      'Play fantasy basketball with ownable NBA player cards - Sorare',
  },
  metadatasDescription: {
    id: 'Landing.NBA.metadatasDescription',
    defaultMessage:
      "Play Sorare's free fantasy basketball game. Collect, buy, sell & compete with ownable digital player cards from all 30 NBA teams to win cash, ETH, tickets, more",
  },
});

const heroAssets = {
  background: {
    avif: heroBgAvif,
    pictureUrl: heroBg,
  },
  players: [
    {
      avif: heroPlayersLeftSideAvif,
      pictureUrl: heroPlayersLeftSide,
      height: 474,
      width: 466,
    },
    {
      avif: heroPlayersRightSideAvif,
      pictureUrl: heroPlayersRightSide,
      height: 474,
      width: 466,
    },
  ],
};

const scoutAssets = {
  background: {
    avif: scoutBgAvif,
    pictureUrl: scoutBg,
  },
  players: {
    avif: scoutPlayersAvif,
    pictureUrl: scoutPlayers,
  },
};

const playAssets = {
  background: {
    avif: playBackgroundAvif,
    pictureUrl: playBackground,
  },
  lineup: {
    avif: lineupAvif,
    pictureUrl: lineup,
  },
};

const winAssets = [
  {
    name: 'plane',
    src: plane,
    width: 320,
    height: 257,
    gridArea: '1 / 1 / 2 / 2',
  },
  {
    name: 'jersey',
    src: jersey,
    width: 274,
    height: 229,
    gridArea: '2 / 1 / 3 / 2',
  },
  {
    name: 'allStars',
    src: allStars,
    width: 320,
    height: 257,
    gridArea: '1 / 2 / 3 / 3',
  },
  {
    name: 'coin',
    src: coin,
    width: 285,
    height: 257,
    gridArea: '2 / 3 / 3 / 4',
  },
  {
    name: 'cards',
    src: cards,
    width: 320,
    height: 228,
    gridArea: '1 / 4 / 3 / 5',
  },
  {
    name: 'box',
    src: box,
    width: 320,
    height: 257,
    gridArea: '1 / 5 / 2 / 6',
  },
  {
    name: 'ticket',
    src: ticket,
    width: 320,
    height: 257,
    gridArea: '2 / 5 / 3 / 6',
  },
  {
    name: 'allStars2',
    src: allStars,
    width: 320,
    height: 257,
    gridArea: '1 / 6 / 3 / 7',
  },
  {
    name: 'coin2',
    src: coin,
    width: 285,
    height: 257,
    gridArea: '1 / 7 / 2 / 8',
  },
  {
    name: 'cards2',
    src: cards,
    width: 320,
    height: 228,
    gridArea: '1 / 8 / 3 / 9',
  },
];

type Props = {
  isRoot?: boolean;
};

const NbaPage = ({ isRoot }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const track = useEvents();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: 'https://frontend-assets.sorare.com/meta/social-nba-2026.jpg',
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      track('View Homepage Disconnected', {
        landing_variant: 'NBA',
      });
    }
  }, [isLoggedIn, track]);

  if (isLoggedIn) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_HOME)}
        replace
        keepSearchParams
      />
    );
  }

  return (
    <WalletLanding
      showFAQ={!isRoot}
      assets={{
        heroAssets,
        scoutAssets,
        winAssets,
        playAssets,
      }}
      league={League.NBA}
      leaderboards={[
        So5LeaderboardType.NBA_LIMITED_CHAMPION,
        So5LeaderboardType.NBA_UNIQUE_CHAMPION,
        So5LeaderboardType.NBA_RARE_CHAMPION,
      ]}
      leagueLogo={<Img src={leagueLogo} alt="NBA" width="240" height="80" />}
    />
  );
};

export default NbaPage;
